import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ActionButton } from '@/shared/ui/action-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
  DrawerTrigger
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';

import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Radix sizes ScrollArea content in a `display: table` box, so `w-full` fields overflow
// the panel. The `!` is load-bearing: that display is an inline style.
const SCROLL_VIEWPORT_BLOCK = '[&_[data-radix-scroll-area-viewport]>div]:block!';

interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  trigger?: React.ReactNode;
  onApply?: () => void;
  onReset?: () => void;
  isApplyDisabled?: boolean;
  isResetDisabled?: boolean;
}

export function FilterPanel({
  open,
  onOpenChange,
  title,
  children,
  trigger,
  onApply,
  onReset,
  isApplyDisabled,
  isResetDisabled
}: FilterPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const handleApply = () => {
    onApply?.();
    onOpenChange(false);
  };

  const handleReset = () => {
    onReset?.();
  };

  const FilterActions = () => (
    <div className="flex gap-2 pt-4">
      <ActionButton intent="dismiss" onClick={handleReset} className="flex-1" disabled={isResetDisabled}>
        {t('common.reset', 'Reset')}
      </ActionButton>
      <ActionButton intent="primary" onClick={handleApply} className="flex-1" disabled={isApplyDisabled}>
        {t('common.apply', 'Apply')}
      </ActionButton>
    </div>
  );

  if (isMobile) {
    const drawerContent = (
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <ScrollArea className={cn('flex-1 px-4', SCROLL_VIEWPORT_BLOCK)}>
          <div className="pb-4">{children}</div>
        </ScrollArea>
        <DrawerFooter>
          <FilterActions />
          <DrawerClose asChild>
            <ActionButton intent="dismiss" className="w-full">
              {t('gen.cancel.label', 'Cancel')}
            </ActionButton>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    );

    if (trigger) {
      return (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerTrigger asChild>{trigger}</DrawerTrigger>
          {drawerContent}
        </Drawer>
      );
    }

    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {drawerContent}
      </Drawer>
    );
  }

  const popoverContent = (
    <PopoverContent className="w-80" align="end">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium leading-none">{title}</h4>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <ScrollArea className={cn('max-h-[60vh]', SCROLL_VIEWPORT_BLOCK)}>{children}</ScrollArea>
        <FilterActions />
      </div>
    </PopoverContent>
  );

  if (trigger) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        {popoverContent}
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {popoverContent}
    </Popover>
  );
}
